import { AuthService } from '../../auth/authService'
import { getEnvSettings } from '../configurationServices'
import { getSecureToken, getEnterpriseIdFromToken } from '../get-secure-token'

// ─── Types ───────────────────────────────────────────────────────────────────

export type ScreenStatus = 'online' | 'offline' | 'crash_loop' | 'disconnected'

export interface ScreenItem {
  screen_id: string
  display: string
  screen_name?: string
  hostname: string
  status: ScreenStatus
  uptime: string
  restarts: number
  last_heartbeat: string
  last_heartbeat_full: string
  event_type_raw: string
  server_id?: string
  server_name?: string
  screen_config_id?: string
}

export interface AgencyData {
  agency_id: string
  agency_name: string
  screens: ScreenItem[]
  has_db_error: boolean
  is_critical: boolean
}

export interface SummaryData {
  total_screens: number
  online: number
  offline: number
  crash_loop: number
  disconnected: number
  db_error_agencies: number
}

export interface StatusDistributionPoint {
  agency: string
  online: number
  offline: number
  crash_loop: number
  disconnected: number
}

export interface UptimeTrendPoint {
  time: string
  uptime: number
}

export interface HealthDistribution {
  online: number
  offline: number
  crash_loop: number
  disconnected: number
}

export interface CriticalAlert {
  id: string
  type: 'screen_offline' | 'crash_loop'
  title: string
  detail: string
  timestamp: string
}

export interface SystemHealthMetric {
  label: string
  status: 'stable' | 'active' | 'warning' | 'error'
  percentage?: number
}

export interface ScreenGroupStats {
  screen_id: string
  screen_name: string
  total: number
  online: number
  failing: number
  percentage: number
}

export interface DashboardData {
  summary: SummaryData
  consortium_name: string
  last_sync: string
  agencies: AgencyData[]
  status_distribution: StatusDistributionPoint[]
  uptime_trend: UptimeTrendPoint[]
  health_distribution: HealthDistribution
  critical_alerts: CriticalAlert[]
  system_health: SystemHealthMetric[]
  agencies_count: number
  servers_count: number
  screen_groups: ScreenGroupStats[]
}

// ─── Endpoint Response Interfaces ──────────────────────────────────────────────

export interface MonitorScreenItem {
  _id: string
  agency_id: string
  display_id: number
  enterprise_id: string
  crash_loop: boolean
  host_name: string
  last_heartbeat: string
  restart_count: number
  screen_id: string
  status: string
  uptime_seconds: number
  event_type?: string
  license_key: string
  screen_name: string
  agency_name: string
  agency_active: boolean
  server_name: string
  server_active: boolean
  server_id?: string
}

export interface MonitorResponseSummary {
  total_screens?: number
  active_screens?: number
  inactive_screens?: number
}

export interface MonitorResponseData {
  summary: MonitorResponseSummary
  items: MonitorScreenItem[]
}

export interface MonitorResponse {
  data: MonitorResponseData
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Formats uptime from seconds to HH:MM:SS format
 */
const formatUptime = (seconds?: number): string => {
  if (!seconds) return '00:00:00'
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  const pad = (num: number) => String(num).padStart(2, '0')
  return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`
}

/**
 * Formats ISO date string to Dominican local time string (12-hour format)
 */
const formatLastHeartbeat = (isoString?: string): { time: string; fullDate: string } => {
  if (!isoString) return { time: '-', fullDate: 'Sin fecha' }
  try {
    // Append 'Z' to force JS to parse it as UTC if it's missing
    const validIso = isoString.endsWith('Z') ? isoString : `${isoString}Z`
    const date = new Date(validIso)

    if (isNaN(date.getTime())) return { time: isoString, fullDate: isoString }

    const time = date.toLocaleTimeString('es-DO', {
      hour: '2-digit',
      minute: '2-digit',
      // second: '2-digit',
      hour12: true,
    })
    const fullDate = date.toLocaleDateString('es-DO', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    return { time, fullDate }
  } catch {
    return { time: isoString, fullDate: isoString }
  }
}

/**
 * Standardizes raw API status string to standard ScreenStatus
 */
const mapStatus = (status: string, crashLoop: boolean): ScreenStatus => {
  const s = (status || '').toLowerCase()
  if (crashLoop || s === 'crash_loop') return 'crash_loop'
  if (s === 'online') return 'online'
  if (s === 'offline') return 'offline'
  if (s === 'disconnected') return 'disconnected'
  return 'offline' // fallback default
}

// ─── Service ──────────────────────────────────────────────────────────────────

/**
 * Fetches monitors data and parses it for the Dashboard UI
 */
export const fetchScreenDashboardData = async (enterpriseId?: string): Promise<DashboardData> => {
  const tokenData = await getSecureToken()
  const token = tokenData?.access_token
  const authService = new AuthService()
  const user = await authService.getUser()

  const tokenEnterpriseId = getEnterpriseIdFromToken()
  const effectiveEnterpriseId = enterpriseId || tokenEnterpriseId

  const { NOC_DISPLAY_BASE_URL } = getEnvSettings()

  try {
    await fetch(`${NOC_DISPLAY_BASE_URL}/screens-monitor/5S68J-99JHK/clear-events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-user': user?.profile.sub || '',
        Authorization: `Bearer ${token}`,
        ...(effectiveEnterpriseId && { 'X-Placebet-Enterprise': effectiveEnterpriseId }),
      },
      body: '',
    })
  } catch (error) {
    console.warn('Error clearing screen events:', error)
  }

  const response = await fetch(`${NOC_DISPLAY_BASE_URL}/screens-monitor/AGHXY-5G9Y4/events`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-user': user?.profile.sub || '',
      Authorization: `Bearer ${token}`,
      ...(effectiveEnterpriseId && { 'X-Placebet-Enterprise': effectiveEnterpriseId }),
    },
    body: '',
  })

  if (!response.ok) {
    throw new Error(`Error al obtener monitoreo de pantallas (${response.status})`)
  }

  const result: MonitorResponse = await response.json()

  if (!result || !result.data || !Array.isArray(result.data.items)) {
    throw new Error('Respuesta del servidor inválida')
  }

  const items = result.data.items

  // Sort items by display_id ascending so they appear in order (D1, D2, D3...)
  items.sort((a, b) => (a.display_id || 0) - (b.display_id || 0))

  // 1. Group items by agency
  const agenciesMap: Record<string, AgencyData> = {}
  items.forEach((item) => {
    const agencyId = item.agency_id || 'unknown_agency'
    const agencyName = item.agency_name || agencyId

    if (!agenciesMap[agencyId]) {
      agenciesMap[agencyId] = {
        agency_id: agencyId,
        agency_name: agencyName,
        screens: [],
        has_db_error: false,
        is_critical: false,
      }
    }

    const heartbeat = formatLastHeartbeat(item.last_heartbeat)

    const mappedScreen: ScreenItem = {
      screen_id: item._id, // Key used in React lists
      display: `D${item.display_id}`,
      screen_name: item.screen_name || '-',
      hostname: item.host_name || '-',
      status: mapStatus(item.status, item.crash_loop),
      uptime: formatUptime(item.uptime_seconds),
      restarts: item.restart_count || 0,
      last_heartbeat: heartbeat.time,
      last_heartbeat_full: heartbeat.fullDate,
      event_type_raw: item.event_type || '',
      server_id: item.server_id || item.server_name || 'unknown',
      server_name: item.server_name || '-',
      screen_config_id: item.screen_id,
    }

    agenciesMap[agencyId].screens.push(mappedScreen)
  })

  const agencies = Object.values(agenciesMap).sort((a, b) =>
    a.agency_name.localeCompare(b.agency_name, undefined, { numeric: true, sensitivity: 'base' })
  )

  // 2. Set dynamic states for agencies
  agencies.forEach((agency) => {
    agency.is_critical = agency.screens.some((s) => s.status === 'crash_loop')
  })

  // 3. Compute status metrics from active display statuses
  let itemsOnlineCount = 0
  let offlineCount = 0
  let crashLoopCount = 0
  let itemsDisconnectedCount = 0

  items.forEach((item) => {
    const rawStatus = (item.status || '').toUpperCase()

    if (rawStatus === 'ONLINE') itemsOnlineCount++
    else if (rawStatus === 'OFFLINE') offlineCount++
    else if (rawStatus === 'DISCONNECTED') itemsDisconnectedCount++

    if (item.event_type === 'CRASH_LOOP') {
      crashLoopCount++
    }
  })

  // 4. Compute critical alerts dynamically
  const critical_alerts: CriticalAlert[] = []
  items.forEach((item) => {
    const status = mapStatus(item.status, item.crash_loop)
    if (status === 'offline' || status === 'crash_loop') {
      critical_alerts.push({
        id: `alert-${item._id}`,
        type: status === 'crash_loop' ? 'crash_loop' : 'screen_offline',
        title: status === 'crash_loop' ? 'Bucle de Reinicio Detectado' : 'Pantalla Fuera de Línea',
        detail: `Agencia: ${item.agency_name || 'Desconocida'} | Display: D${item.display_id} | Host: ${
          item.host_name || '-'
        }`,
        timestamp: formatLastHeartbeat(item.last_heartbeat).time,
      })
    }
  })

  const apiSummary = result.data.summary || {}
  const totalDisplays = apiSummary.total_screens ?? items.length
  const onlineCount = itemsOnlineCount
  const disconnectedCount = apiSummary.inactive_screens ?? itemsDisconnectedCount

  const summary: SummaryData = {
    total_screens: totalDisplays,
    online: onlineCount,
    offline: offlineCount,
    crash_loop: crashLoopCount,
    disconnected: disconnectedCount,
    db_error_agencies: 0, // Reserved for future implementation
  }

  const health_distribution: HealthDistribution = {
    online: onlineCount,
    offline: offlineCount,
    crash_loop: crashLoopCount,
    disconnected: disconnectedCount,
  }

  const last_sync = new Date().toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })

  const activeAgenciesSet = new Set(items.filter((item) => item.agency_active).map((item) => item.agency_id))
  const agenciesCount = new Set(items.map((item) => item.agency_id)).size || agencies.length
  const serversCount = new Set(items.map((item) => item.server_id || item.server_name || 'unknown')).size

  // Group items by screen_id (using screen_name for display, screen_id for grouping)
  const screenGroupsMap: Record<string, { screen_name: string; total: number; online: number }> = {}
  items.forEach((item) => {
    const screenId = item.screen_id || 'unknown_screen'
    const screenName = item.screen_name || 'Sin Nombre'
    const isOnline = (item.status || '').toUpperCase() === 'ONLINE'

    if (!screenGroupsMap[screenId]) {
      screenGroupsMap[screenId] = {
        screen_name: screenName,
        total: 0,
        online: 0,
      }
    }
    screenGroupsMap[screenId].total++
    if (isOnline) {
      screenGroupsMap[screenId].online++
    }
  })

  const screen_groups: ScreenGroupStats[] = Object.entries(screenGroupsMap).map(([screenId, stats]) => {
    const failing = stats.total - stats.online
    const percentage = stats.total > 0 ? Math.round((stats.online / stats.total) * 100) : 0
    return {
      screen_id: screenId,
      screen_name: stats.screen_name,
      total: stats.total,
      online: stats.online,
      failing,
      percentage,
    }
  })

  screen_groups.sort((a, b) =>
    a.screen_name.localeCompare(b.screen_name, undefined, { numeric: true, sensitivity: 'base' })
  )

  return {
    summary,
    consortium_name: 'Consorcio ABC1',
    last_sync,
    agencies,
    status_distribution: [],
    uptime_trend: [],
    health_distribution,
    critical_alerts,
    system_health: [
      { label: 'NETWORK_LATENCY', status: 'stable' },
      { label: 'DB_CONNECTION', status: 'stable' },
      { label: 'AGENT_SYNC', status: 'stable' },
    ],
    agencies_count: agenciesCount,
    servers_count: serversCount,
    screen_groups,
  }
}
